import React, { useState, useEffect } from 'react';
import { Mail, Copy, CheckCircle2, AlertCircle, Download, MessageSquare, Calendar, Phone, MapPin } from 'lucide-react';
import { getStatusColor, formatServiceM8Text } from './adminUtils';

const BookingsTab = ({ BACKEND_URL }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [printBooking, setPrintBooking] = useState(null);
  const [printType, setPrintType] = useState(null);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/bookings`);
      if (!response.ok) throw new Error('Failed to fetch bookings');
      const data = await response.json();
      setBookings(data);
    } catch (err) {
      console.error('Error fetching bookings:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, []);

  // Reset print state after printing
  useEffect(() => {
    const afterPrint = () => { setPrintBooking(null); setPrintType(null); };
    window.addEventListener('afterprint', afterPrint);
    return () => window.removeEventListener('afterprint', afterPrint);
  }, []);

  const copyToClipboard = (booking) => {
    const text = formatServiceM8Text(booking);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(booking.booking_id);
      setTimeout(() => setCopiedId(null), 3000);
    });
  };

  const handleDownloadPDF = (booking, type = 'invoice') => {
    setPrintBooking(booking);
    setPrintType(type);
    setTimeout(() => { window.print(); }, 100);
  };

  const handleEmailShare = (booking) => {
    const subject = encodeURIComponent(`Booking Confirmation - ${booking.farm_name} - ${booking.booking_id}`);
    const body = encodeURIComponent(formatServiceM8Text(booking));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleSMSShare = (booking) => {
    const smsBody = `Booking ${booking.booking_id} - ${booking.farm_name}\nContact: ${booking.contact_name}\nDate: ${booking.preferred_date}\nTotal: $${booking.total_cost_incl_gst.toFixed(2)}\nServices: ${booking.services.map(s => s.service_name).join(', ')}`;
    window.location.href = `sms:?body=${encodeURIComponent(smsBody)}`;
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking request? This action cannot be undone.')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete booking');
      setBookings(bookings.filter(b => b.id !== bookingId));
      alert('Booking deleted successfully');
    } catch (err) {
      alert('Error deleting booking: ' + err.message);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch =
      booking.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.farm_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.booking_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#1a4d2e] mx-auto"></div>
        <p className="mt-4 text-gray-500">Loading bookings...</p>
      </div>
    );
  }

  return (
    <>
      {/* Alert Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Email Integration Temporarily Unavailable</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Due to SMTP authentication issues, automatic emails to ServiceM8 are currently disabled.
                Please use the &quot;Copy for ServiceM8&quot; button to manually forward booking details.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by name, farm, email, or booking ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="bookings-search-input"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a4d2e] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                data-testid="bookings-status-filter"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a4d2e] focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
            <p className="text-sm text-gray-600">Total Bookings</p>
            <p data-testid="total-bookings-count" className="text-2xl font-bold text-gray-900">{bookings.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-gray-600">Pending</p>
            <p className="text-2xl font-bold text-gray-900">{bookings.filter(b => b.status === 'pending').length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
            <p className="text-sm text-gray-600">Confirmed</p>
            <p className="text-2xl font-bold text-gray-900">{bookings.filter(b => b.status === 'confirmed').length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
            <p className="text-sm text-gray-600">Completed</p>
            <p className="text-2xl font-bold text-gray-900">{bookings.filter(b => b.status === 'completed').length}</p>
          </div>
        </div>
      </div>

      {/* Bookings List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {filteredBookings.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No bookings found</h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Bookings will appear here once customers make reservations'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <div key={booking.booking_id} data-testid={`booking-card-${booking.booking_id}`} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-[#1a4d2e]">{booking.farm_name}</h3>
                          <p className="text-gray-600">{booking.contact_name}</p>
                          <p className="text-xs text-gray-500 mt-1">Booking ID: {booking.booking_id}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(booking.status)}`}>
                          {booking.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-4">
                        <div className="flex items-center text-gray-700"><Mail className="h-4 w-4 mr-2 text-[#ff6b00]" />{booking.email}</div>
                        <div className="flex items-center text-gray-700"><Phone className="h-4 w-4 mr-2 text-[#ff6b00]" />{booking.phone}</div>
                        <div className="flex items-center text-gray-700"><MapPin className="h-4 w-4 mr-2 text-[#ff6b00]" />{booking.region}</div>
                        <div className="flex items-center text-gray-700"><Calendar className="h-4 w-4 mr-2 text-[#ff6b00]" />{booking.preferred_date}</div>
                      </div>
                      <div className="bg-[#f4f4f0] rounded-lg p-3 mb-4">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Services:</p>
                        <ul className="space-y-1">
                          {booking.services.map((service, idx) => (
                            <li key={idx} className="text-sm text-gray-700">
                              - <span className="font-medium">{service.service_name}</span> - {service.num_animals} animals
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div>
                          <p className="text-xs text-green-700">Total (incl. GST)</p>
                          <p className="text-2xl font-bold text-green-900">${booking.total_cost_incl_gst.toFixed(2)}</p>
                        </div>
                        <div className="text-right text-xs text-green-700">
                          <p>Excl. GST: ${booking.total_cost_excl_gst.toFixed(2)}</p>
                          <p>GST: ${booking.gst_amount.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col space-y-2">
                      <button onClick={() => copyToClipboard(booking)} data-testid={`copy-servicem8-${booking.booking_id}`} className="flex items-center justify-center px-4 py-2 bg-[#ff6b00] text-white rounded-lg hover:bg-[#e55a00] transition-colors font-semibold whitespace-nowrap">
                        {copiedId === booking.booking_id ? (<><CheckCircle2 className="h-4 w-4 mr-2" />Copied!</>) : (<><Copy className="h-4 w-4 mr-2" />Copy for ServiceM8</>)}
                      </button>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => handleDownloadPDF(booking, 'quote')} className="flex items-center justify-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-sm" title="Download Quote PDF with Pricing">
                          <Download className="h-4 w-4 mr-1" /> Quote PDF
                        </button>
                        <button onClick={() => handleDownloadPDF(booking, 'workorder')} className="flex items-center justify-center px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-semibold text-sm" title="Download Work Order (No Pricing)">
                          <Download className="h-4 w-4 mr-1" /> Work Order
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => handleEmailShare(booking)} className="flex items-center justify-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold text-sm" title="Share via Email">
                          <Mail className="h-4 w-4 mr-1" /> Email
                        </button>
                        <button onClick={() => handleSMSShare(booking)} className="flex items-center justify-center px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold text-sm" title="Share via SMS">
                          <MessageSquare className="h-4 w-4 mr-1" /> SMS
                        </button>
                      </div>
                      <button onClick={() => setSelectedBooking(selectedBooking?.booking_id === booking.booking_id ? null : booking)} className="px-4 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#2d6b47] transition-colors font-semibold">
                        {selectedBooking?.booking_id === booking.booking_id ? 'Hide Details' : 'View Details'}
                      </button>
                      <button onClick={() => handleDeleteBooking(booking.id)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold" title="Delete this booking request">
                        Remove Request
                      </button>
                    </div>
                  </div>
                  {selectedBooking?.booking_id === booking.booking_id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Full Address:</h4>
                        <p className="text-gray-700 mb-4">{booking.address}</p>
                        {booking.notes && (<><h4 className="font-semibold text-gray-900 mb-2">Additional Notes:</h4><p className="text-gray-700 mb-4">{booking.notes}</p></>)}
                        <h4 className="font-semibold text-gray-900 mb-2">Booking Created:</h4>
                        <p className="text-gray-700">{new Date(booking.created_at).toLocaleString('en-NZ')}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-bold text-blue-900 mb-3 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" /> How to Forward Bookings to ServiceM8
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
            <li>Click the &quot;Copy for ServiceM8&quot; button on any booking</li>
            <li>Open your email client and compose a new email</li>
            <li>Send to: <strong>3439ff@inbox.servicem8.com</strong> and <strong>admin@kiwicourts.co.nz</strong></li>
            <li>Paste the copied booking details into the email body</li>
            <li>Send the email - ServiceM8 will automatically create a job from the email</li>
          </ol>
          <p className="mt-4 text-xs text-blue-700">Once SMTP authentication is resolved, this process will be fully automated.</p>
        </div>
      </div>

      {/* Print Content (hidden, shown only during print) */}
      {printBooking && (
        <div id="print-booking" style={{ display: 'none' }}>
          <style>{`
            @media print {
              .main-dashboard { display: none !important; }
              #print-booking { display: block !important; position: absolute; left: 0; top: 0; width: 100%; background: white; }
              @page { size: A4 portrait; margin: 12mm; }
            }
          `}</style>
          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.3', padding: '15px', background: 'white' }}>
            <div style={{ textAlign: 'center', marginBottom: '12px', borderBottom: '2px solid #1a4d2e', paddingBottom: '8px' }}>
              <h1 style={{ color: '#1a4d2e', fontSize: '20px', margin: '0 0 3px 0' }}>Measured Livestock</h1>
              <p style={{ color: '#ff6b00', fontSize: '13px', margin: '0' }}>{printType === 'workorder' ? 'Work Order' : 'Booking Confirmation'}</p>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <h2 style={{ color: '#1a4d2e', fontSize: '14px', marginBottom: '6px', borderBottom: '1px solid #e5ddd5', paddingBottom: '3px', fontWeight: 'bold' }}>Customer Information</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <tbody>
                  <tr><td style={{ padding: '3px 0', fontWeight: 'bold', width: '120px' }}>Farm Name:</td><td style={{ padding: '3px 0' }}>{printBooking.farm_name}</td><td style={{ padding: '3px 0', fontWeight: 'bold', width: '100px' }}>Region:</td><td style={{ padding: '3px 0' }}>{printBooking.region}</td></tr>
                  <tr><td style={{ padding: '3px 0', fontWeight: 'bold' }}>Contact Name:</td><td style={{ padding: '3px 0' }}>{printBooking.contact_name}</td><td style={{ padding: '3px 0', fontWeight: 'bold' }}>Phone:</td><td style={{ padding: '3px 0' }}>{printBooking.phone}</td></tr>
                  <tr><td style={{ padding: '3px 0', fontWeight: 'bold' }}>Email:</td><td style={{ padding: '3px 0' }} colSpan="3">{printBooking.email}</td></tr>
                  <tr><td style={{ padding: '3px 0', fontWeight: 'bold' }}>Address:</td><td style={{ padding: '3px 0' }} colSpan="3">{printBooking.address}</td></tr>
                </tbody>
              </table>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <h2 style={{ color: '#1a4d2e', fontSize: '14px', marginBottom: '6px', borderBottom: '1px solid #e5ddd5', paddingBottom: '3px', fontWeight: 'bold' }}>Booking Details</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <tbody>
                  <tr><td style={{ padding: '3px 0', fontWeight: 'bold', width: '120px' }}>Preferred Date:</td><td style={{ padding: '3px 0' }}>{printBooking.preferred_date}</td><td style={{ padding: '3px 0', fontWeight: 'bold', width: '100px' }}>Preferred Time:</td><td style={{ padding: '3px 0' }}>{printBooking.preferred_time || 'Not specified'}</td></tr>
                  <tr><td style={{ padding: '3px 0', fontWeight: 'bold' }}>Created:</td><td style={{ padding: '3px 0' }} colSpan="3">{new Date(printBooking.created_at).toLocaleString('en-NZ')}</td></tr>
                </tbody>
              </table>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <h2 style={{ color: '#1a4d2e', fontSize: '14px', marginBottom: '6px', borderBottom: '1px solid #e5ddd5', paddingBottom: '3px', fontWeight: 'bold' }}>Services Requested</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e5ddd5', fontSize: '11px' }}>
                <thead><tr style={{ backgroundColor: '#f4f4f0' }}><th style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #1a4d2e', fontSize: '11px' }}>Service</th><th style={{ padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid #1a4d2e', fontSize: '11px', width: '80px' }}>Animals</th></tr></thead>
                <tbody>
                  {printBooking.services.map((service, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e5ddd5' }}><td style={{ padding: '5px 8px' }}>{service.service_name}</td><td style={{ padding: '5px 8px', textAlign: 'center' }}>{service.num_animals}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            {printType === 'quote' && (
              <div style={{ marginBottom: '10px' }}>
                <h2 style={{ color: '#1a4d2e', fontSize: '14px', marginBottom: '6px', borderBottom: '1px solid #e5ddd5', paddingBottom: '3px', fontWeight: 'bold' }}>Pricing</h2>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <tbody>
                    <tr><td style={{ padding: '3px 0' }}>Subtotal (excl. GST):</td><td style={{ padding: '3px 0', textAlign: 'right' }}>${printBooking.total_cost_excl_gst.toFixed(2)}</td></tr>
                    <tr><td style={{ padding: '3px 0' }}>GST (15%):</td><td style={{ padding: '3px 0', textAlign: 'right' }}>${printBooking.gst_amount.toFixed(2)}</td></tr>
                    <tr style={{ borderTop: '2px solid #1a4d2e' }}><td style={{ padding: '6px 0', fontSize: '14px', fontWeight: 'bold', color: '#1a4d2e' }}>TOTAL (incl. GST):</td><td style={{ padding: '6px 0', textAlign: 'right', fontSize: '16px', fontWeight: 'bold', color: '#ff6b00' }}>${printBooking.total_cost_incl_gst.toFixed(2)}</td></tr>
                  </tbody>
                </table>
              </div>
            )}
            {printBooking.notes && (
              <div style={{ marginBottom: '10px' }}>
                <h2 style={{ color: '#1a4d2e', fontSize: '14px', marginBottom: '6px', borderBottom: '1px solid #e5ddd5', paddingBottom: '3px', fontWeight: 'bold' }}>Additional Notes</h2>
                <p style={{ padding: '6px', backgroundColor: '#f4f4f0', margin: '0', fontSize: '11px' }}>{printBooking.notes}</p>
              </div>
            )}
            <div style={{ marginTop: '15px', paddingTop: '8px', borderTop: '1px solid #e5ddd5', textAlign: 'center', color: '#666', fontSize: '9px' }}>
              <p style={{ margin: '0' }}>Measured Livestock - Professional Trait Collection Service - Reliable - Accurate - Farmer-Friendly</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingsTab;
